import { toJS } from 'mobx';
import {
// applySnapshot, getSnapshot,
cast, getParent, types } from 'mobx-state-tree';
import { FEE_UNIT_TYPES } from 'src/constants/feesSetting';
import { YEmailValidateDefault } from 'src/utils/formValidation/yupValidators';
const UtilityUnit = types.model({
  utilityId: types.string,
  utilityType: types.string,
  utilityCap: types.maybeNull(types.number)
});
const EnrollResidentInUnitValidation = types.model({
  billingStartDateOnly: types.boolean,
  mailingAddress: types.boolean,
  residentialPortalAccessDate: types.boolean
});
const EnrollResidentInUnitRoommatesValidation = types.model({
  email: types.maybeNull(types.string),
  firstName: types.maybeNull(types.string),
  lastName: types.maybeNull(types.string)
});
const EnrollResidentRoommateUnit = types.model({
  email: types.string,
  firstName: types.string,
  lastName: types.string
});
const EnrollResidentInUnit = types.model({
  automaticallySendPaperInvitations: types.boolean,
  automaticallySendPaperStatements: types.boolean,
  billingStartDateOnly: types.maybeNull(types.string),
  firstName: types.string,
  customAdminFee: types.maybeNull(types.number),
  customLateFee: types.maybeNull(types.number),
  billingType: types.string,
  lastName: types.string,
  leaseAddendumFileId: types.maybeNull(types.string),
  leaseAddendumFileName: types.string,
  leaseStartDateOnly: types.maybeNull(types.string),
  mailingAddress: types.string,
  notes: types.string,
  occupancy: types.number,
  phone: types.string,
  primaryEmail: types.string,
  residentialPortalAccessDate: types.maybeNull(types.string),
  roommates: types.array(EnrollResidentRoommateUnit),
  statementCap: types.maybeNull(types.number),
  utilities: types.array(UtilityUnit),
  unitId: types.string
});
const enrollResidentInUnitInitialData = {
  automaticallySendPaperInvitations: true,
  automaticallySendPaperStatements: true,
  billingStartDateOnly: null,
  billingType: 'Bill',
  customAdminFee: null,
  firstName: '',
  lastName: '',
  leaseAddendumFileId: '',
  leaseAddendumFileName: '',
  leaseStartDateOnly: null,
  mailingAddress: '',
  notes: '',
  occupancy: 1,
  phone: '',
  primaryEmail: '',
  residentialPortalAccessDate: null,
  roommates: [],
  statementCap: null,
  utilities: [],
  unitId: ''
};
const InUnitAmenities = types.model({
  inUnitAmenityId: types.string,
  inUnitAmenityOptionId: types.string
});
const SearchedUnits = types.model({
  amenityIds: types.array(types.string),
  availableFromDateOnly: types.maybeNull(types.string),
  id: types.string,
  inUnitAmenities: types.optional(types.array(InUnitAmenities), []),
  name: types.string,
  occupancy: types.number,
  squareFootage: types.number,
  status: types.maybeNull(types.string),
  type: types.string,
  utilityIds: types.array(types.string),
  utilityOptOutIds: types.optional(types.array(types.string), [])
});
const EnrollResident = types.model('EnrollResident', {
  customAdminFeeValue: types.maybeNull(types.number),
  customLateFeeValue: types.maybeNull(types.number),
  customLateFeeSignValue: types.optional(types.string, FEE_UNIT_TYPES.fixedAmount),
  customSearchedUnits: types.optional(types.array(SearchedUnits), []),
  enrollResidentInUnit: types.optional(EnrollResidentInUnit, enrollResidentInUnitInitialData),
  enrollResidentInUnitRoommatesValidation: types.optional(types.array(EnrollResidentInUnitRoommatesValidation), []),
  enrollResidentUnitValidation: types.optional(EnrollResidentInUnitValidation, {
    billingStartDateOnly: false,
    residentialPortalAccessDate: true,
    mailingAddress: true
  }),
  searchedUnitsData: types.optional(types.array(SearchedUnits), []),
  selectedPropertyValue: types.maybeNull(types.string)
}).views(store => ({
  get customAdminFee() {
    return store.customAdminFeeValue;
  },
  get customLateFee() {
    return store.customLateFeeValue;
  },
  get customLateFeeSign() {
    return store.customLateFeeSignValue;
  },
  get customUnits() {
    return toJS(store.customSearchedUnits);
  },
  get selectedProperty() {
    return store.selectedPropertyValue;
  },
  getUtilityCap(id) {
    const currentUtilityIndex = store.enrollResidentInUnit.utilities.findIndex(item => item.utilityId === id);
    if (currentUtilityIndex === -1) return null;
    return store.enrollResidentInUnit.utilities[currentUtilityIndex].utilityCap;
  },
  getUtilityType(id) {
    const currentUtilityIndex = store.enrollResidentInUnit.utilities.findIndex(item => item.utilityId === id);
    if (currentUtilityIndex === -1) return null;
    return store.enrollResidentInUnit.utilities[currentUtilityIndex].utilityType;
  },
  isValidField(id) {
    return !!store.enrollResidentUnitValidation[id];
  }
})).actions(store => {
  // let afterLoadState;

  return {
    assignSearchedUnits(resp) {
      try {
        store.customSearchedUnits = cast(resp.response);
        store.searchedUnitsData = cast(resp.response);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    addRoommateUnit() {
      try {
        store.enrollResidentInUnit.roommates.push({
          email: '',
          firstName: '',
          lastName: ''
        });
        store.enrollResidentInUnitRoommatesValidation.push({
          email: null,
          firstName: 'Field is required',
          lastName: 'Field is required'
        });
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    changeInUnitValue(field, data) {
      try {
        store.enrollResidentInUnit[field] = data;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    changeRoommateUnitValue(index, filed, value) {
      try {
        store.enrollResidentInUnit.roommates[index][filed] = value;
        if (filed === 'email') {
          store.enrollResidentInUnitRoommatesValidation[index][filed] = YEmailValidateDefault.isValidSync(value) ? null : 'Invalid email address.';
        } else {
          store.enrollResidentInUnitRoommatesValidation[index][filed] = value.trim().length < 2 ? "".concat(filed === 'firstName' ? 'First name' : 'Last name', " should be at least 2 characters.") : value.trim().length > 50 ? "".concat(filed === 'firstName' ? 'First name' : 'Last name', " should not exceed 50 characters.") : null;
        }
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    changeUtilityCap(utilityId, cap) {
      try {
        const currentUtilityIndex = store.enrollResidentInUnit.utilities.findIndex(item => item.utilityId === utilityId);
        store.enrollResidentInUnit.utilities[currentUtilityIndex].utilityCap = cap;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    changeUtilityType(utilityId, utilityType) {
      try {
        const currentUtilityIndex = store.enrollResidentInUnit.utilities.findIndex(item => item.utilityId === utilityId);
        store.enrollResidentInUnit.utilities[currentUtilityIndex].utilityType = utilityType;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    clearData() {
      try {
        store.customAdminFeeValue = null;
        store.customLateFeeValue = null;
        store.customLateFeeSignValue = FEE_UNIT_TYPES.fixedAmount;
        store.customSearchedUnits = cast([]);
        store.enrollResidentInUnit = cast(enrollResidentInUnitInitialData);
        store.enrollResidentInUnitRoommatesValidation = cast([]);
        store.enrollResidentUnitValidation = cast({
          billingStartDateOnly: false,
          residentialPortalAccessDate: true,
          mailingAddress: true
        });
        store.searchedUnitsData = cast([]);
        store.selectedPropertyValue = null;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    customSearch(data) {
      try {
        const currentValue = data.trim();
        store.customSearchedUnits = cast([]);
        if (currentValue === '') store.customSearchedUnits = cast(toJS(store.searchedUnitsData));else store.searchedUnitsData.forEach(item => {
          if (item.name.toLowerCase().includes(currentValue.toLowerCase())) store.customSearchedUnits.push(toJS(item));
        });
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    removeRoommateUnit(index) {
      try {
        store.enrollResidentInUnit.roommates.splice(index, 1);
        store.enrollResidentInUnitRoommatesValidation.splice(index, 1);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setCustomAdminFee(value) {
      try {
        store.customAdminFeeValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setCustomLateFee(value) {
      try {
        store.customLateFeeValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setCustomLateFeeSign(value) {
      try {
        store.customLateFeeSignValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setSelectedProperty(value) {
      try {
        store.selectedPropertyValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setUtilities(data) {
      try {
        store.enrollResidentInUnit.utilities = cast([]);
        if (!!data) {
          const _getParent = getParent(store),
            addNewProperty = _getParent.addNewProperty;
          addNewProperty.utilitiesSetupV2.selectedUtilities.map(utility => {
            store.enrollResidentInUnit.utilities.push({
              utilityId: utility.utilityId,
              utilityType: 'RatioBilling'
            });
          });
        }
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    updateLeaseAddendumAttachment(fileId) {
      try {
        store.enrollResidentInUnit.leaseAddendumFileId = fileId;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    updateLeaseAddendumAttachmentName(fileName) {
      try {
        store.enrollResidentInUnit.leaseAddendumFileName = fileName;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    validateValue(id, value) {
      try {
        store.enrollResidentUnitValidation[id] = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    }
  };
});
export const enrollResident = types.optional(EnrollResident, {});